'use client';

import { useEffect, useState } from 'react';

import Link from 'next/link';
import { usePathname } from 'next/navigation';

import Logo from '@/components/icons/Logo';
import { ModeToggle } from '@/components/ui/Themes/ModeToggle';
import type { PostSummary } from '@/lib/mdx';

// import { SearchDialog } from './SearchDialog';
import { ShareButtonWrapper } from './ShareButtonWrapper';
import UserNav from './UserNav';
import type { User } from 'next-auth';

interface NavBlogProps {
  posts: PostSummary[];
  user?: User | null;
  onSignOut?: () => void;
}

export default function NavBlog({
  user,
  onSignOut
}: Omit<NavBlogProps, 'posts'>) {
  const pathname = usePathname();
  const [isBlogSubdomain, setIsBlogSubdomain] = useState(false);

  useEffect(() => {
    const host = window.location.hostname;
    if (
      host.includes('blog.bioinfometrics.com') ||
      host.includes('blog.localhost')
    ) {
      setIsBlogSubdomain(true);
    }
  }, []);

  const blogPath = isBlogSubdomain ? '' : '/blog';

  const linkClasses = (path: string) => {
    // Ensure path matching works even if path doesn't have the blog prefix
    const cleanPathname = isBlogSubdomain
      ? pathname
      : pathname.replace(/^\/blog/, '') || '/';
    const cleanTarget = path.replace(/^\/blog/, '') || '/';
    const isActive = cleanPathname === cleanTarget;

    return `relative px-4 py-1.5 text-sm font-medium transition-all duration-300 rounded-full ${
      isActive
        ? 'bg-brand-dark/10 text-brand-dark dark:bg-brand-dark/30 dark:text-brand-dark-text-primary shadow-sm'
        : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
    }`;
  };

  return (
    <>
      <header className='border-border bg-background/95 sticky top-0 z-50 border-b backdrop-blur-sm'>
        <div className='mx-auto max-w-6xl px-2 sm:px-4 lg:px-8'>
          <div className='flex h-14 items-center justify-between sm:h-16'>
            <div className='flex grow items-center lg:grow-0'>
              <Logo className='text-brand-dark dark:text-brand-dark-text-primary h-6 w-6 shrink-0 sm:h-8 sm:w-8' />
              <h1
                className='text-brand-dark dark:text-brand-dark-text-primary z-10 ml-2 text-base font-bold sm:ml-3 sm:text-lg md:text-xl lg:absolute lg:top-1/2 lg:left-1/2 lg:ml-0 lg:-translate-x-1/2 lg:-translate-y-1/2 font-serif'
                style={{ fontFamily: "'Lora', serif" }}>
                <Link
                  href={`${blogPath}/`}
                  className='flex items-center gap-2 whitespace-nowrap transition-opacity duration-200 hover:opacity-80'>
                  Bioinfometrics
                </Link>
              </h1>
            </div>
            <div className='z-20 flex items-center space-x-2 sm:space-x-4'>
              <ModeToggle />
              <ShareButtonWrapper />
              <UserNav
                user={user || null}
                onSignOut={onSignOut || (() => {})}
              />
            </div>
          </div>
        </div>
      </header>

      <nav className='border-border/40 bg-background/60 sticky top-14 z-40 border-b backdrop-blur-md sm:top-16'>
        <div className='mx-auto max-w-6xl px-4 py-2 sm:px-6 lg:px-8'>
          <div className='flex flex-wrap justify-center gap-2'>
            <Link
              href={`${blogPath}/`}
              className={linkClasses('/blog')}>
              Home
            </Link>
            <Link
              href={`${blogPath}/archive`}
              className={linkClasses('/blog/archive')}>
              Archive
            </Link>
            <Link
              href={`${blogPath}/about`}
              className={linkClasses('/blog/about')}>
              About
            </Link>
          </div>
        </div>
      </nav>
    </>
  );
}
